import { Button, type ButtonProps } from "$components/button/button";
import {
  type MenuItem,
  PopoverMenu,
} from "$components/popover-menu/popover-menu";
import { styled, useMediaQuery, useTheme } from "@material-ui/core";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import { type ReactNode, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { ActionBarContainer } from "./action-bar-container";
import { ActionBarSpeedDial } from "./action-bar-speed-dial";
import type { ActionBarVariant, ActionItem } from "./action-item";

const MoreActionsButton = styled(Button)({
  marginLeft: "12px",
});

/**
 * The action bar keeps its own semantic variant names; map them to the
 * canonical Button's MUI `variant` + theme `color`.
 */
const VARIANT_PROPS: Record<
  ActionBarVariant,
  { variant: ButtonProps["variant"]; color: ButtonProps["color"] }
> = {
  primary: { variant: "contained", color: "primary" },
  secondary: { variant: "contained", color: "secondary" },
  tertiary: { variant: "outlined", color: "primary" },
  quarternary: { variant: "text", color: "primary" },
};

type Props = {
  /** Ordered, pre-filtered actions. The first `maxInline` render as buttons. */
  actions: ActionItem[];
  /**
   * How many actions render inline before the rest collapse into the overflow
   * menu. Defaults to showing every action inline.
   */
  maxInline?: number;
  /**
   * Actions that always live in the overflow menu (never inline), appended
   * after any overflowed actions — e.g. the price-list connect/disconnect
   * entries.
   */
  moreActionsItems?: ActionItem[];
  /** Extra inline nodes after the buttons, before "More actions" (e.g. bubbles). */
  trailing?: ReactNode;
  /** Label for the overflow trigger; keep per-consumer to preserve translations. */
  moreActionsLabel?: ReactNode;
  moreActionsTestId?: string;
  className?: string;
  /** Ref to the underlying container element (e.g. for bottom-bar registration). */
  containerRef?: React.Ref<HTMLElement>;
};

export const ActionBar = ({
  actions,
  maxInline = actions.length,
  moreActionsItems = [],
  trailing,
  moreActionsLabel = (
    <FormattedMessage
      id="actionBar.moreActions"
      defaultMessage="More actions"
    />
  ),
  moreActionsTestId,
  className,
  containerRef,
}: Props) => {
  const intl = useIntl();
  const theme = useTheme();
  // Mobile-first: the base layout is a single bottom-right SpeedDial FAB holding
  // every action; from `md` up we enhance to the inline row + overflow menu.
  // Screens needing a bespoke small-screen layout compose `ActionBarContainer`
  // themselves (e.g. the price-list edit/review bars) rather than this component.
  // `defaultMatches: true` keeps the desktop layout as the fallback when there's
  // no `matchMedia` (SSR / jsdom), so server render and unmocked tests get the
  // inline row; real clients resolve the real viewport.
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"), {
    defaultMatches: true,
  });

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // The overflow menu only exists on the desktop enhancement; when the viewport
  // drops below `md` its anchor button unmounts, so close the menu and drop the
  // stale anchor.
  useEffect(() => {
    if (!isDesktop) {
      setIsMenuOpen(false);
      setAnchorEl(null);
    }
  }, [isDesktop]);

  const renderButton = (action: ActionItem) => (
    <Button
      key={action.id}
      {...VARIANT_PROPS[action.variant ?? "secondary"]}
      startIcon={action.icon}
      onClick={action.onClick}
      disabled={action.disabled}
      data-testid={action.testId}
    >
      <FormattedMessage id={action.id} defaultMessage={action.defaultMessage} />
    </Button>
  );

  const inlineActions = actions.slice(0, maxInline);
  const overflowActions = actions.slice(maxInline);

  // Overflowed actions and the always-in-menu items render through the same
  // PopoverMenu, so map both to its MenuItem shape the same way. Clicking any
  // of them closes the menu after running the action's own handler.
  const toMenuItem = (action: ActionItem): MenuItem => ({
    id: action.id,
    defaultMessage: action.defaultMessage,
    icon: action.icon ?? null,
    onClick: (e) => {
      action.onClick?.(e);
      setIsMenuOpen(false);
    },
  });

  const menuItems = [...overflowActions, ...moreActionsItems].map(toMenuItem);
  const fabActions = [...actions, ...moreActionsItems];

  return (
    <>
      {/* One persistent container keeps `containerRef` attached across
          breakpoint changes so the bottom-bar height re-registers correctly.
          Below `md` it's hidden (the SpeedDial takes over) but stays mounted. */}
      <ActionBarContainer
        ref={containerRef}
        className={className}
        style={isDesktop ? undefined : { display: "none" }}
      >
        {isDesktop && (
          <>
            {inlineActions.map(renderButton)}
            {trailing}
            {menuItems.length > 0 && (
              <MoreActionsButton
                variant="text"
                startIcon={<MoreVertIcon />}
                onClick={(e) => {
                  setAnchorEl(e.currentTarget);
                  setIsMenuOpen(true);
                }}
                data-testid={moreActionsTestId}
              >
                {moreActionsLabel}
              </MoreActionsButton>
            )}
          </>
        )}
      </ActionBarContainer>

      {!isDesktop
        ? fabActions.length > 0 && (
            <ActionBarSpeedDial
              actions={fabActions}
              ariaLabel={intl.formatMessage({
                id: "actionBar.speedDial",
                defaultMessage: "Actions",
              })}
              trailing={trailing}
            />
          )
        : isMenuOpen &&
          anchorEl &&
          menuItems.length > 0 && (
            <PopoverMenu
              anchorEl={anchorEl}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "bottom", horizontal: "right" }}
              style={{ transform: "translate(18px, -18px)" }}
              open={isMenuOpen}
              onClose={() => setIsMenuOpen(false)}
              primaryMenuItems={menuItems}
            />
          )}
    </>
  );
};
